/**
 * Front script (vanilla JS)
 */
const ROOT_SELECTOR = '.wp-block-gt-gt-menu-burger';
const BODY_OPEN_CLASS = 'gt-menu-burger-open';
const OPEN_CLASS = 'is-open';
const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const roots = new Set();
const scrollPositions = new WeakMap();
let hasBoundGlobalClick = false;

function setExpanded(root, isOpen) {
	const toggles = root.querySelectorAll('[data-gt-menu-burger="toggle"]');
	toggles.forEach((btn) => btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));

	const content = root.querySelector('[data-gt-menu-burger="content"]');
	if (content) {
		content.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
	}

	root.querySelectorAll('[data-gt-menu-burger-show="open"]').forEach((el) => {
		if (isOpen) el.removeAttribute('hidden');
		else el.setAttribute('hidden', '');
	});
	root.querySelectorAll('[data-gt-menu-burger-show="closed"]').forEach((el) => {
		if (isOpen) el.setAttribute('hidden', '');
		else el.removeAttribute('hidden');
	});
}

function focusFirstInPanel(root) {
	const content = root.querySelector('[data-gt-menu-burger="content"]');
	if (!content) return;
	const focusables = content.querySelectorAll(FOCUSABLE_SELECTOR);
	focusables?.[0]?.focus?.({ preventScroll: true });
}

function focusToggle(root) {
	const toggle = root.querySelector('[data-gt-menu-burger="toggle"]');
	toggle?.focus?.({ preventScroll: true });
}

function openMenu(root) {
	if (root.classList.contains(OPEN_CLASS)) return;
	scrollPositions.set(root, window.scrollY);
	root.classList.add(OPEN_CLASS);
	document.body.classList.add(BODY_OPEN_CLASS);
	setExpanded(root, true);
	setTimeout(() => focusFirstInPanel(root), 10);
}

function closeMenu(root) {
	if (!root.classList.contains(OPEN_CLASS)) return;
	root.classList.remove(OPEN_CLASS);
	document.body.classList.remove(BODY_OPEN_CLASS);
	setExpanded(root, false);
	window.scrollTo(0, scrollPositions.get(root) || 0);
	setTimeout(() => focusToggle(root), 10);
}

function toggleMenu(root) {
	if (root.classList.contains(OPEN_CLASS)) closeMenu(root);
	else openMenu(root);
}

function trapFocus(e, root) {
	if (!root.classList.contains(OPEN_CLASS)) return;
	if (e.key === 'Escape' || e.keyCode === 27) {
		e.preventDefault();
		closeMenu(root);
		return;
	}
	if (!(e.key === 'Tab' || e.keyCode === 9)) return;

	const content = root.querySelector('[data-gt-menu-burger="content"]');
	if (!content) return;
	const focusables = content.querySelectorAll(FOCUSABLE_SELECTOR);
	if (!focusables.length) return;

	const first = focusables[0];
	const last = focusables[focusables.length - 1];
	if (e.shiftKey) {
		if (document.activeElement === first) {
			last.focus();
			e.preventDefault();
		}
	} else if (document.activeElement === last) {
		first.focus();
		e.preventDefault();
	}
}

function bindGlobalHandlers() {
	if (hasBoundGlobalClick) return;
	hasBoundGlobalClick = true;

	document.addEventListener('click', (e) => {
		roots.forEach((root) => {
			if (!root.classList.contains(OPEN_CLASS)) return;
			if (root.contains(e.target)) return;
			closeMenu(root);
		});
	});
}

function initMenuBurger(root) {
	roots.add(root);
	bindGlobalHandlers();

	// Initial state.
	setExpanded(root, root.classList.contains(OPEN_CLASS));

	const toggleButtons = root.querySelectorAll('[data-gt-menu-burger="toggle"]');
	toggleButtons.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			toggleMenu(root);
		});
	});

	const closeButtons = root.querySelectorAll('[data-gt-menu-burger="close"]');
	closeButtons.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			closeMenu(root);
		});
	});

	const content = root.querySelector('[data-gt-menu-burger="content"]');
	if (content) {
		content.addEventListener('keydown', (e) => trapFocus(e, root));

		// Handle anchor links inside the menu
		content.addEventListener('click', (e) => {
			const link = e.target.closest('a[href]');
			if (!link) return;

			const href = link.getAttribute('href');
			if (!href) return;

			const url = new URL(href, window.location.href);
			const isSamePage =
				url.origin === window.location.origin &&
				url.pathname === window.location.pathname;

			if (isSamePage && url.hash) {
				// Same-page anchor link (e.g. #piscine)
				e.preventDefault();
				// Close menu and restore scroll
				root.classList.remove(OPEN_CLASS);
				document.body.classList.remove(BODY_OPEN_CLASS);
				setExpanded(root, false);
				window.scrollTo(0, scrollPositions.get(root) || 0);
				// Scroll to the anchor target
				const target = document.querySelector(url.hash);
				if (target) {
					target.scrollIntoView({ behavior: 'smooth' });
				}
				history.pushState(null, '', url.hash);
			}
			// For external/other links, let the browser handle navigation normally
		});
	}
}

function initAll() {
	document.querySelectorAll(ROOT_SELECTOR).forEach((root) => initMenuBurger(root));
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAll);
} else {
	initAll();
}
